import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LifeBuoy, Send, Loader2 } from "lucide-react";

const CATEGORIES = [
  { v: "question", l: "Question" },
  { v: "probleme", l: "Problème technique" },
  { v: "modification", l: "Demande de modification" },
  { v: "facturation", l: "Facturation" },
  { v: "autre", l: "Autre" },
];

const STATUS_FR = {
  nouveau: { label: "Nouveau", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  en_cours: { label: "En cours", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  repondu: { label: "Répondu", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ferme: { label: "Fermé", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function ProSupport() {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("question");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/pro/support");
      setTickets(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3 || message.trim().length < 5) {
      toast.error("Merci d'indiquer un sujet et un message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/pro/support", { subject: subject.trim(), message: message.trim(), category });
      toast.success("Votre demande a bien été envoyée à l'équipe PROXIreduc-28.");
      setSubject(""); setMessage(""); setCategory("question");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="pro-support-page">
      <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
        <LifeBuoy className="text-[#E05638]" size={26} /> Aide & support
      </h1>
      <p className="text-slate-500 text-sm mt-1">Une question ou un problème ? Notre équipe locale vous répond rapidement.</p>

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="pro-support-form">
          <div>
            <label className="text-xs font-semibold text-slate-600">Sujet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ex : Problème avec ma facture" data-testid="pro-support-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Catégorie</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="pro-support-category">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
                      className="w-full rounded-md border border-slate-200 p-3 text-sm" placeholder="Décrivez votre demande…" data-testid="pro-support-message" />
          </div>
          <Button type="submit" disabled={sending} className="bg-[#E05638] hover:bg-[#c94a2e] w-full" data-testid="pro-support-submit">
            {sending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />} Envoyer ma demande
          </Button>
        </form>

        <div className="lg:col-span-3 space-y-3">
          <h2 className="font-semibold text-slate-800">Mes demandes</h2>
          {tickets.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-500 text-sm" data-testid="pro-support-empty">
              Vous n'avez encore aucune demande.
            </div>
          ) : tickets.map((t) => {
            const st = STATUS_FR[t.status] || { label: t.status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
            return (
              <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`pro-support-ticket-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900">{t.subject}</div>
                  <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${st.cls}`}>{st.label}</span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">{(t.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{t.message}</p>
                {t.reply && (
                  <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-slate-700">
                    <div className="text-xs font-bold text-emerald-700 mb-1">Réponse de l'équipe</div>
                    <p className="whitespace-pre-wrap">{t.reply}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
